<template>
    <div class="py-3 relative border-1 border-black/20">

        <div class="progress mb-2 h-[2px] py-2 rounded-[6px] relative cursor-pointer"
             ref="progress"
             @mouseover="handleVisible = true" @mouseout="handleVisible = false" @click.stop="handleClick"
             @mouseup="isDragging = false"
             @mousemove="handleMove">
            <div
                class="progress__inner absolute left-0 h-2 top-1/2 -mt-[3px] bg-player-contrast z-20 pointer-events-none"
                :style="{ width: percentage + '%', 'transition-[width]': !isDragging }">
            </div>

            <div
                @mousedown.stop="isDragging = true"
                class="progress__bullet absolute block right-[-5px] top-1/2 -mt-[5px] -ml-1.5 w-3 h-3 bg-player-contrast rounded-full z-50"
                :class="{ 'cursor-grab': !isDragging, 'cursor-grabbing': isDragging, 'transition-[left]': !isDragging }"
                :style="{ left: percentage + '%' } "></div>

            <div
                class="progress__handle cursor-pointer absolute left-0 right-0 h-2 top-1/2 -mt-[3px] z-20 pointer-events-none">
                <div v-show="handleVisible"
                     class="progress__handle__inner pointer-events-none absolute left-0 h-2 bg-white/50"
                     @click.stop="handleClick" :style="{ width: currentHandleWidth + '%' }"></div>
            </div>

            <div
                class="progress__handle cursor-pointer absolute left-0 right-0 h-2 top-1/2 -mt-[3px] z-10 pointer-events-none">
                <div v-show="handleVisible"
                     class="progress__handle__inner pointer-events-none absolute left-0 h-2 bg-player-contrast"
                     @click.stop="handleClick" :style="{ width: handleWidth + '%' }"></div>
            </div>

            <div class="absolute left-0 right-0 flex flex-row">
                <div
                    class="absolute h-3 -top-[5px] border-x-[2px] border-y-[5px] rounded-[6px] border-player-background cursor-pointer z-30 bg-white/20"
                    :style="placeholderMarkerStyle()">
                    <div
                        class="z-20 h-[2px] block bg-transparent w-full absolute top-1/2 -translate-y-1/2 rounded-[6px] cursor-pointer"></div>

                    <div class="progress__corners"></div>
                </div>

                <template v-if="track.chapters?.length" v-for="(chapter, key) in track.chapters">
                    <div
                        class="absolute z-30 h-2 border-x-[2px] -top-[3px] border-player-background border-y-[3px] group hover:border-y-0 bg-white/20 rounded-[6px] cursor-pointer"
                        :style="markerStyle(chapter)">

                        <tippy follow-cursor="horizontal" :hide-on-click="false" theme="light" interactive>
                            <div
                                class="-z-30 block bg-transparent w-full absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-hover:h-2 rounded-[6px]">
                                <button :id="'chapter-' + key"
                                        class="z-50 h-2 absolute -top-[5px] cursor-pointer w-full block transition"></button>

                            </div>

                            <div class="progress__corners group-hover:top-0"></div>

                            <template #content>
                                <div class="text-center px-2 py-1 text-[#512B9F]">
                                    <span class="block text-[12px] leading-[18px] font-medium">{{ formatTime(timeSeek) }}</span>

                                    <span class="block text-[14px] leading-[22px] font-bold whitespace-nowrap">{{ chapter.title }}</span>
                                </div>
                            </template>
                        </tippy>
                    </div>
                </template>
            </div>
        </div>

        <div class="absolute bottom-0 left-0 text-[10px] text-player-contrast" :aria-label="__('Current Time')">
            {{ formatTime(currentTime) }}
        </div>

        <div class="absolute bottom-0 right-0 text-[10px] text-player-contrast" :aria-label="__('Duration')">
            {{ formatTime(duration) }}
        </div>
    </div>
</template>

<script setup>
import __ from "../helpers";
import { formatTime } from "../useWaveform";
import { computed, onMounted, ref, watch } from "vue";
import { Tippy } from "vue-tippy";

const props = defineProps({
    podcast: {
        type: Object,
    },
    track: {
        type: Object,
        required: true
    },
    startAt: {
        type: [Number, String, null],
        required: false,
    },
    preview: {
        type: [Boolean, null],
        required: false,
        default: false
    }
})

const emit = defineEmits(['ended', 'loaded', 'timeupdate', 'scroll', 'seeked', 'seeking'])

const progress = ref(null)
const audio = ref(null)
const duration = ref(props.track.audio.duration)
const currentTime = ref(props.startAt || 0)

const isLoading = ref(false)

const isPlaying = ref(false)
const isMuted = ref(false)

const percentage = computed(() => {
    // clamp to 100
    return Math.min((currentTime.value / duration.value) * 100, 100)
})

const seek = computed(() => {
    const podcastId = props.podcast.hash_id;
    const episodeId = props.track.hash_id;

    const progress = window.localStorage.getItem(
        podcastId + "-" + episodeId
    );

    return progress
})

watch(() => props.track, () => {
    // Clear previous event listeners
    audio.value.removeEventListener('loadedmetadata', loaded)
    audio.value.removeEventListener('timeupdate', timeupdate)
    audio.value.removeEventListener('ended', ended)
    audio.value.removeEventListener('seeked', seeked)
    audio.value.removeEventListener('seeking', seeking)

    isLoading.value = true
    isPlaying.value = false
    duration.value = props.track.audio.duration
    audio.value.src = props.track.audio.url
    currentTime.value = parseFloat(seek.value)

    if (seek.value) {
        setTimeout(() => {
            currentTime.value = parseFloat(seek.value)
        }, 300)
    }

    // Assign new event listeners
    audio.value.addEventListener('loadedmetadata', loaded)
    audio.value.addEventListener('timeupdate', timeupdate)
    audio.value.addEventListener('ended', ended)
    audio.value.addEventListener('seeked', seeked)
    audio.value.addEventListener('seeking', seeking)
})

onMounted(() => {
    isLoading.value = true

    duration.value = props.track.audio.duration

    audio.value = document.createElement('audio')
    audio.value.preload = 'none'
    audio.value.src = props.track.audio.url

    if (seek.value) {
        currentTime.value = seek.value
    }

    audio.value.addEventListener('loadedmetadata', loaded)
    audio.value.addEventListener('timeupdate', timeupdate)
    audio.value.addEventListener('ended', ended)
    audio.value.addEventListener('seeked', seeked)
    audio.value.addEventListener('seeking', seeking)
})

function loaded() {
    isLoading.value = false

    emit('loaded')
}

function timeupdate() {
    currentTime.value = audio.value.currentTime || currentTime.value

    if (!isDragging.value && !isLoading.value) {
        emit('timeupdate')
    }
}

function ended() {
    isPlaying.value = false

    emit('ended')
}

const handleVisible = ref(false)
const handleWidth = ref(0)
const currentHandleWidth = ref(0)
const timeSeek = computed(() => {
    return Math.round(handleWidth.value / 100 * duration.value);
})
function handleMove(event) {
    const rect = event.currentTarget.getBoundingClientRect()
    const x = event.clientX - rect.left
    handleWidth.value = (x / rect.width) * 100

    currentHandleWidth.value = handleWidth.value >= percentage.value ? 0 : handleWidth.value;

    handleDrag(event)
}

function handleClick(event) {
    if (isDragging.value) {
        return
    }

    const rect = event.currentTarget.getBoundingClientRect()

    // Check if audio is loaded via audio ready state
    if (audio.value.readyState < 3) {
        // Load audio
        audio.value.load()

        audio.value.addEventListener('loadedmetadata', function handleClickBeforeLoad() {
            audio.value.removeEventListener('loadedmetadata', handleClickBeforeLoad)

            const x = event.clientX - rect.left
            const percentage = (x / rect.width) * 100
            audio.value.currentTime = (percentage / 100) * duration.value

            emit('scroll')
        })
    } else {
        const x = event.clientX - rect.left
        const percentage = (x / rect.width) * 100
        audio.value.currentTime = (percentage / 100) * duration.value

        emit('scroll')
    }
}

const isDragging = ref(false)

function handleDrag(event) {
    if (isDragging.value) {
        const progressRect = progress.value.getBoundingClientRect()

        const x = event.clientX - progressRect.left
        const percentage = (x / progressRect.width) * 100

        currentTime.value = (percentage / 100) * duration.value

        // Clamp currentTime to duration and 0
        currentTime.value = Math.min(Math.max(currentTime.value, 0), duration.value)
    }
}

function play() {
    if (audio.value.readyState < 3) {
        audio.value.load()

        audio.value.addEventListener('loadedmetadata', function playBeforeLoad() {
            audio.value.removeEventListener('loadedmetadata', playBeforeLoad)

            audio.value.currentTime = currentTime.value

            audio.value.play()

            isPlaying.value = true
        })
    } else {
        audio.value.play()

        isPlaying.value = true
    }
}

function pause() {
    audio.value.pause()

    isPlaying.value = false
}

function seekTo(time) {
    audio.value.currentTime = time
    currentTime.value = time
}

function mute() {
    audio.value.muted = true

    isMuted.value = true
}

function unmute() {
    audio.value.muted = false

    isMuted.value = false
}

function skip(seconds) {
    audio.value.currentTime += seconds
}

function back(seconds) {
    audio.value.currentTime -= seconds
}

function playbackRate(rate) {
    audio.value.playbackRate = rate
}

function seeked() {
    emit('seeked')
}

function seeking() {
    emit('seeking')
}

function markerStyle(chapter) {
    if (props.track.chapters?.length < 1) {
        return { left: '100%' }
    }

    // Calculate width based on next chapter
    const nextChapter = props.track.chapters[props.track.chapters.indexOf(chapter) + 1]
    let width = nextChapter ? (nextChapter.from_time - chapter.from_time) / duration.value * 100 : 100

    // If no next chapter, calculate width based on end of track
    if (!nextChapter) {
        width = (duration.value - chapter?.from_time) / duration.value * 100
    }

    return {
        left: `${chapter?.from_time / duration.value * 100}%`,
        width: `${width}%`,
    }
}

function placeholderMarkerStyle() {
    const firstMarkerStyle = markerStyle(props.track.chapters[0])

    return {
        left: 0,
        width: firstMarkerStyle.left,
    }
}

const currentChapter = computed(() => {
    // Find the chapter that the current time is in between from_time and next chapters from_time
    return props.track.chapters.findIndex((chapter, index) => {
        const nextChapter = props.track.chapters[index + 1]

        if (nextChapter) {
            return currentTime.value >= chapter.from_time && currentTime.value < nextChapter.from_time
        }

        return currentTime.value >= chapter.from_time
    })
})

defineExpose({
    duration,
    currentTime,
    isPlaying,
    isMuted,
    currentChapter,
    play,
    pause,
    seekTo,
    mute,
    unmute,
    skip,
    back,
    playbackRate,
})

</script>

<style scoped>
.progress__corners {
    @apply absolute;
    z-index: 100;
    width: 6px;
    height: 8px;
    top: -3px;
    left: -6px;
}

.progress__corners::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 4px solid var(--player-background);
    z-index: 100;
}

.progress__corners::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid var(--player-background);
    z-index: 100;
}
</style>
